/**
 * 7-day shift timeline for one PA (layout from the timeline Ali shared with the team).
 * Axis 11:00 → 03:00 Baghdad; positions are % of that span so the row stretches with the card.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { PaShift, ScanMark, StopMark } from '../../services/workingHours.api';
import { formatTimeBaghdad as fmt } from '../../utils/date';
import { AXIS_HOURS, HOUR, axisPct } from './axis';

const TICKS = ['11:00', '13:00', '15:00', '17:00', '19:00', '21:00', '23:00', '01:00', '03:00'];
const MARK = 'absolute -translate-x-1/2 motion-safe:transition-transform motion-safe:duration-150 motion-safe:hover:scale-125';

const at = (iso: string) => Date.parse(iso);
const span = (day: string, a: number, b: number): CSSProperties => {
  const left = axisPct(day, a);
  return { left: `${left}%`, width: `${Math.max(0.4, axisPct(day, b) - left)}%` };
};
const tick = (i: number): CSSProperties => ({ left: `${((i * 2) / AXIS_HOURS) * 100}%` });
const dayLabel = (day: string) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', timeZone: 'Asia/Baghdad' });

function scheduledSpan(s: PaShift): [number, number] | null {
  if (!s.scheduled) return null;
  const a = Date.parse(`${s.day}T${s.scheduled.start}:00+03:00`);
  let b = Date.parse(`${s.day}T${s.scheduled.end}:00+03:00`);
  if (b <= a) b += 24 * HOUR;
  return [a, b];
}
const lastMark = (s: PaShift) =>
  Math.max(...(s.marks?.stops.map((m) => at(m.at)) ?? []), ...(s.marks?.scans.map((m) => at(m.to)) ?? []));

function summary(s: PaShift) {
  if (s.notes.includes('no work')) return 'no work';
  const until = s.end ? fmt(s.end) : s.status === 'on shift' ? 'now' : '?';
  return `${s.stops} stops (${s.pickups} pk · ${s.rtoDrops} RTO) · ${s.start ? `${fmt(s.start)}–${until}` : 'no start'}`;
}

function ScanIcon({ day, m }: { day: string; m: ScanMark }) {
  const label = `${m.kind === 'L' ? 'RTO load scan' : 'Drop-off scan'} · ${fmt(m.from)}${m.to !== m.from ? `–${fmt(m.to)}` : ''} · ${m.orders} order${m.orders === 1 ? '' : 's'}`;
  const style = { left: `${axisPct(day, at(m.kind === 'L' ? m.from : m.to))}%` };
  return m.kind === 'L'
    ? <span title={label} aria-label={label} style={style} className={`${MARK} -bottom-2 h-0 w-0 border-x-[6px] border-b-[10px] border-x-transparent border-b-emerald-800`} />
    : <span title={label} aria-label={label} style={style} className={`${MARK} -bottom-1.5 h-2.5 w-2.5 bg-emerald-800`} />;
}

function StopIcon({ day, m }: { day: string; m: StopMark }) {
  const label = `${m.kind === 'P' ? 'Pickup' : 'RTO drop-off'} · ${fmt(m.at)}${m.tasks > 1 ? ` · ${m.tasks} tasks` : ''}`;
  const style = { left: `${axisPct(day, at(m.at))}%` };
  return m.kind === 'P'
    ? <span title={label} aria-label={label} style={style} className={`${MARK} top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-blue-600 ring-2 ring-white`} />
    : <span title={label} aria-label={label} style={style} className={`${MARK} top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-emerald-500 ring-2 ring-white`} />;
}

function Row({ s }: { s: PaShift }) {
  const sched = scheduledSpan(s);
  const barEnd = s.end ? at(s.end) : s.status === 'on shift' ? lastMark(s) : null;
  return (
    <>
      <div className="text-[13px] tabular-nums text-gray-700">{dayLabel(s.day)}</div>
      <div className="relative h-9 rounded-lg bg-gray-50">
        {TICKS.map((t, i) => <span key={t} aria-hidden className="absolute -inset-y-2 w-px bg-black/[0.04]" style={tick(i)} />)}
        {sched && <span title={`Scheduled ${s.scheduled!.start}–${s.scheduled!.end}`} className="absolute inset-y-0 rounded-md bg-emerald-100/70" style={span(s.day, sched[0], sched[1])} />}
        {s.start && barEnd !== null && Number.isFinite(barEnd) && (
          <span title={`Actual shift ${fmt(s.start)}–${s.end ? fmt(s.end) : 'now'}`}
            className={`absolute inset-y-1 rounded-md border-2 border-emerald-800 ${s.status === 'on shift' ? 'border-dashed' : ''}`}
            style={span(s.day, at(s.start), barEnd)} />
        )}
        {s.marks?.idle.map((g) => (
          <span key={g.from} title={`Idle ${fmt(g.from)}–${fmt(g.to)}`} className="absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-orange-500" style={span(s.day, at(g.from), at(g.to))} />
        ))}
        {s.marks?.scans.map((m) => <ScanIcon key={`${m.kind}${m.from}`} day={s.day} m={m} />)}
        {s.marks?.stops.map((m) => <StopIcon key={`${m.kind}${m.at}`} day={s.day} m={m} />)}
      </div>
      <div className="truncate text-[12px] tabular-nums text-gray-500" title={s.notes.join(' · ')}>{summary(s)}</div>
    </>
  );
}

export function TimelineLegend() {
  const item = (icon: ReactNode, text: string) => <span className="inline-flex items-center gap-2">{icon}{text}</span>;
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12px] text-gray-500">
      {item(<span className="h-3 w-6 rounded bg-emerald-100" />, 'Scheduled shift')}
      {item(<span className="h-3 w-6 rounded border-2 border-emerald-800" />, 'Actual shift')}
      {item(<span className="h-0 w-0 border-x-[6px] border-b-[10px] border-x-transparent border-b-emerald-800" />, 'RTO load scan')}
      {item(<span className="h-2.5 w-2.5 bg-emerald-800" />, 'Drop-off scan')}
      {item(<span className="h-2.5 w-2.5 rounded-full bg-blue-600" />, 'Pickup')}
      {item(<span className="h-2 w-2 rotate-45 bg-emerald-500" />, 'RTO drop-off')}
      {item(<span className="h-[3px] w-6 rounded-full bg-orange-500" />, 'Idle gap (60 min+)')}
    </div>
  );
}

export default function ShiftTimeline({ days }: { days: PaShift[] }) {
  return (
    <div>
      <TimelineLegend />
      <p className="mt-3 text-[12px] text-gray-500">One row per day. Hover any mark for its time.</p>
      <div className="mt-4 grid grid-cols-[64px_minmax(0,1fr)_260px] items-center gap-x-4 gap-y-1.5">
        <div />
        <div className="relative h-5 text-[11px] tabular-nums text-gray-500">
          {TICKS.map((t, i) => <span key={t} className="absolute -translate-x-1/2" style={tick(i)}>{t}</span>)}
        </div>
        <div />
        {days.map((s) => <Row key={s.day} s={s} />)}
      </div>
    </div>
  );
}
